import { Injectable } from "@angular/core";
import { CrAlert, CrAlertState, CrRedirectRoute, CrSyncService } from "./crsync.service";
import { DeviceClass } from "app/models/device.model";
import { NIGHTOWL_SUPERVISION_PAUSED } from "app/models/camera.model";

/**
 * One recipient of an alert, as shown to the operator. `name` and `lastSeen`
 * are undefined when the session's team does not know the user, or when the
 * alert server does not report a last seen time for them.
 */
export interface AppRecipient {
  userId: string;
  name: string | undefined;
  isOnline: boolean;
  lastSeen: string | undefined;
  // false when the session's team does not have this user at all --> isOnline
  // and lastSeen say nothing about them
  known: boolean;
}
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from "@angular/cdk/drag-drop";
import { VideoServerService } from "./videoserver.service";
import { ApiService } from "./api.service";
import { PickUpAlertIcon } from "app/pipes/pipes";
import { ActiveCameraVisit } from "app/models/camerasupervision.model";
import moment from "moment";
import { NotificationService } from "./notification.service";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ControlRoomService {
  public fetchAlertHistoryForAlert = new Subject<void>();
  constructor(
    private crSync: CrSyncService,
    private videoServerService: VideoServerService,
    private api: ApiService,
    private pickUpAlertIcon: PickUpAlertIcon,
    private notificationService: NotificationService
  ) {}

  transitionalAlerts: { [alertId: string]: NodeJS.Timeout } = {};

  incomingVisits: any[] = [];
  startedVisits: any[] = [];

  incomingAlerts: any[] = [];
  startedAlerts: CrAlert[] = [];
  forwardedAlerts: any[] = [];
  teamsStartedAlerts: any[] = [];
  teamsForwardedAlerts: any[] = [];

  private _removeAlertById(alerts: CrAlert[], alertId: string): CrAlert | undefined {
    let removedAlert: CrAlert | undefined;
    for (let i = alerts.length - 1; i >= 0; i--) {
      if (alerts[i].alertId === alertId) {
        const [removed] = alerts.splice(i, 1);
        removedAlert = removed;
      }
    }
    return removedAlert;
  }

  private _upsertAlertById(alerts: CrAlert[], alert: CrAlert): void {
    const ix = alerts.findIndex((a) => a.alertId === alert.alertId);
    if (ix === -1) {
      alerts.push(alert);
      return;
    }

    alerts[ix] = alert;
    for (let i = alerts.length - 1; i > ix; i--) {
      if (alerts[i].alertId === alert.alertId) {
        alerts.splice(i, 1);
      }
    }
  }

  private _isUserOrAlias(userId: string): boolean {
    return (
      userId === this.crSync.userId ||
      this.crSync.aliases.findIndex((alias) => alias.userId === userId) !== -1
    );
  }

  /**
   * True when the alert can be forwarded to the EMA app, i.e. someone would
   * receive it. The recipients are the alarm route's redirect targets reported
   * on the alert, and their presence comes from the team of the alert server
   * session the alert arrived on - an alias in the customer's company.
   *
   * Presence we cannot read never stands in the operator's way: while the team
   * state is not known to be up to date, everything counts as sendable.
   *
   * Once it is known, the answer comes from it. A recipient the team does not
   * hold counts as not reachable rather than as a maybe: alarm routes name such
   * users routinely - other user groups, other locations, memberships left
   * behind months ago - and treating each of them as possibly-online leaves the
   * operator with no answer in exactly the configurations where one is worth
   * having. The confirmation carries the doubt instead, listing those
   * recipients as unknown, and sending anyway is always allowed.
   *
   * @param {CrAlert} alert - The alert about to be sent to the app.
   */
  appUsersAvailable(alert: CrAlert): boolean {
    // the operator's own session is not an app recipient, the same exclusion
    // _anyAppUserOnline makes
    const recipients = alert.redirectRecipients.filter((userId) => userId !== alert.userId);

    // a route that reaches nobody reaches nobody whatever presence says, and
    // the alert server answers that for us: no targets at all, or groups whose
    // members have all logged out or been out of contact for hours
    if (alert.redirectRoute !== CrRedirectRoute.Unknown && recipients.length === 0) return false;

    if (!this.crSync.isTeamStateKnown(alert.userId)) return true;

    const anyReachable = recipients.some((userId) => {
      const member = this.crSync.getTeamMember(userId, alert.userId);
      return !!member && member.isOnline;
    });
    if (anyReachable) return true;

    // the alert does not say who else the route reaches, so the whole customer
    // being offline is the only thing that rules out somebody we cannot see
    if (alert.redirectRoute === CrRedirectRoute.Unknown) return this._anyAppUserOnline(alert);

    return false;
  }

  /**
   * True when the alert's app user presence can be relied on: the alert server
   * reports complete presence and the session's team is synchronised. False
   * means every availability answer is a fail-open guess, which is fine for
   * deciding whether to confirm but not for telling the operator a number.
   *
   * @param {CrAlert} alert - The alert shown on the card.
   */
  appPresenceKnown(alert: CrAlert): boolean {
    return this.crSync.isTeamStateKnown(alert.userId);
  }

  /**
   * The alert's recipients as the operator should see them: the alarm route's
   * redirect targets, named from the session's team where they are known to it,
   * with the time each was last seen by the alert server.
   *
   * An empty list means the alarm route has no fallback recipients at all.
   * Undefined recipients (an alert server that does not report them) yield an
   * empty list too, so callers should ask appUsersAvailable first.
   *
   * @param {CrAlert} alert - The alert about to be sent to the app.
   */
  appRecipients(alert: CrAlert): AppRecipient[] {
    return alert.redirectRecipients
      // the operator's own session is not an app recipient, the same exclusion
      // appUsersAvailable makes - listing it would also overcount the card
      .filter((userId) => userId !== alert.userId)
      .map((userId) => {
        const member = this.crSync.getTeamMember(userId, alert.userId);
        return {
          userId,
          // the alert server names a user it cannot resolve "N/A", which is not
          // something to show an operator
          name: member?.name === "N/A" ? undefined : member?.name,
          isOnline: !!member?.isOnline,
          lastSeen: member?.lastSeen,
          known: !!member
        };
      })
      // most recently seen first: that is the recipient the operator's decision
      // hangs on, and it puts the ones never seen at the end
      .sort((a, b) => (b.lastSeen || "").localeCompare(a.lastSeen || ""));
  }

  /**
   * Fallback for alerts that carry no redirect targets: is any app user of the
   * customer online? A superset of the actual recipients, so it only tells the
   * operator that nobody at all is reachable.
   *
   * @param {CrAlert} alert - The alert about to be sent to the app.
   */
  private _anyAppUserOnline(alert: CrAlert): boolean {
    return this.crSync.getTeamMembers(alert.userId).some(
      (member) => member.isOnline && member.userId !== alert.userId
    );
  }

  takeAction(alert: CrAlert) {
    if (
      alert.state === CrAlertState.Available ||
      alert.state === CrAlertState.Reserved
    ) {
      this.crSync.changeAlertState(
        alert.userId,
        alert.alertId,
        CrAlertState.Started
      );
    } else {
      this.crSync.forwardAlert(alert.userId, alert.alertId);
    }
    alert.transitional = true;
    const timer = setTimeout(() => {
      alert.transitional = false;
    }, 5000);
    this.transitionalAlerts[alert.alertId] = timer;
  }

  cancelReservation(alert: CrAlert) {
    this.crSync.changeAlertState(
      alert.userId,
      alert.alertId,
      CrAlertState.Available
    );
    alert.transitional = true;
    const timer = setTimeout(() => {
      alert.transitional = false;
    }, 5000);
    this.transitionalAlerts[alert.alertId] = timer;
  }

  updateAllAlerts(alerts: CrAlert[]): void {
    // re-fill alert lists
    this.incomingAlerts = [];
    this.startedAlerts = [];
    this.teamsStartedAlerts = [];
    alerts.forEach((alert) => {
      if (alert.state === CrAlertState.Started) {
        if (this._isUserOrAlias(alert.startedBy)) {
          this.startedAlerts.push(alert);
        } else {
          this.teamsStartedAlerts.push(alert);
        }
      } else {
        this.incomingAlerts.push(alert);
      }
    });
    // sort the list so reserved alerts are on top
    this.incomingAlerts.sort((a, b) => {
      return a.reserved === b.reserved ? 0 : a.reserved ? -1 : 1;
    });
    // all alerts are up to date --> remove transitional alerts
    for (const alertId in this.transitionalAlerts) {
      clearTimeout(this.transitionalAlerts[alertId]);
    }
    this.transitionalAlerts = {};
  }

  updateOneAlert(alert: CrAlert) {
    const incomingAlertStates = [CrAlertState.Available, CrAlertState.Reserved];
    if (this.transitionalAlerts[alert.alertId] !== undefined) {
      // getting update for transitional alert --> not transitional anymore
      clearTimeout(this.transitionalAlerts[alert.alertId]);
      delete this.transitionalAlerts[alert.alertId];
    }


    let ix: number = this.incomingAlerts.findIndex(
      (a) => a.alertId === alert.alertId
    );
    if (ix !== -1) {
      if (incomingAlertStates.includes(alert.state)) {
        // set in place
        this._removeAlertById(this.startedAlerts, alert.alertId);
        this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
        this._upsertAlertById(this.incomingAlerts, alert);
      } else if (alert.state === CrAlertState.Started) {
        // remove from incoming
        this._removeAlertById(this.incomingAlerts, alert.alertId);
        if (this._isUserOrAlias(alert.startedBy)) {
          // started by current user --> put to started
          this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
          this._upsertAlertById(this.startedAlerts, alert);
          this.fetchAlertHistoryForAlert.next();
        } else {
          // started by other user --> put to team's started
          this._removeAlertById(this.startedAlerts, alert.alertId);
          this._upsertAlertById(this.teamsStartedAlerts, alert);
        }
      }
      return;
    }
    ix = this.startedAlerts.findIndex((a) => a.alertId === alert.alertId);
    if (ix !== -1) {
      const previousNoteCache = this.startedAlerts[ix].noteCache;
      if (incomingAlertStates.includes(alert.state)) {
        // move from started to incoming
        this._removeAlertById(this.startedAlerts, alert.alertId);
        this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
        this._upsertAlertById(this.incomingAlerts, alert);
      } else if (alert.state === CrAlertState.Started) {
        if (this._isUserOrAlias(alert.startedBy)) {
          // started by current user --> set in place, preserve noteCache
          alert.noteCache = previousNoteCache;
          this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
          this._upsertAlertById(this.startedAlerts, alert);
        } else {
          // started by other user --> put to team's started
          this._removeAlertById(this.startedAlerts, alert.alertId);
          this._upsertAlertById(this.teamsStartedAlerts, alert);
        }
      }
      return;
    }
    ix = this.teamsStartedAlerts.findIndex((a) => a.alertId === alert.alertId);
    if (ix !== -1) {
      if (incomingAlertStates.includes(alert.state)) {
        // move from team's started to incoming
        this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
        this._removeAlertById(this.startedAlerts, alert.alertId);
        this._upsertAlertById(this.incomingAlerts, alert);
      } else if (alert.state === CrAlertState.Started) {
        if (this._isUserOrAlias(alert.startedBy)) {
          // started by current user --> put to started
          this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
          this._upsertAlertById(this.startedAlerts, alert);
        } else {
          // started by other user --> set in place
          this._removeAlertById(this.startedAlerts, alert.alertId);
          this._upsertAlertById(this.teamsStartedAlerts, alert);
        }
      }
      return;
    }
    // this is a new alert
    if (incomingAlertStates.includes(alert.state)) {
      this._removeAlertById(this.startedAlerts, alert.alertId);
      this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
      this._upsertAlertById(this.incomingAlerts, alert);
      this.notificationService.notifyNewAlert(alert.header);
    } else if (
      alert.state === CrAlertState.Started &&
      this._isUserOrAlias(alert.startedBy)
    ) {
      this._removeAlertById(this.incomingAlerts, alert.alertId);
      this._removeAlertById(this.teamsStartedAlerts, alert.alertId);
      this._upsertAlertById(this.startedAlerts, alert);
    }
  }

  deleteOneAlert(alertId: string) {
    if (this.transitionalAlerts[alertId] !== undefined) {
      // delete transitional alert
      clearTimeout(this.transitionalAlerts[alertId]);
      delete this.transitionalAlerts[alertId];
    }

    // check all alerts list: if filtered list is shorter, commit to it and return
    let tmp = this.incomingAlerts.filter((a) => a.alertId !== alertId);
    if (tmp.length < this.incomingAlerts.length) {
      this.incomingAlerts = tmp;
      return;
    }
    tmp = this.startedAlerts.filter((a) => a.alertId !== alertId);
    if (tmp.length < this.startedAlerts.length) {
      this.startedAlerts = tmp;
      return;
    }
    tmp = this.teamsStartedAlerts.filter((a) => a.alertId !== alertId);
    if (tmp.length < this.teamsStartedAlerts.length) {
      this.teamsStartedAlerts = tmp;
      return;
    }
    // deleted alert can also be in forwarded alerts if user has dragged
    // it there for forwarding
    this.forwardedAlerts = this.forwardedAlerts.filter(
      (a) => a.alertId !== alertId
    );
  }

  drop(event: CdkDragDrop<CrAlert[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      const alert: CrAlert = event.previousContainer.data[event.previousIndex];
      if (event.container.id === "startedAlertList") {
        const ok = this.crSync.changeAlertState(
          alert.userId,
          alert.alertId,
          CrAlertState.Started
        );
        if (!ok) return;
      } else if (event.container.id === "forwardedAlertList") {
        if (alert.state !== CrAlertState.Started) return;
        this.crSync.forwardAlert(alert.userId, alert.alertId);
      } else {
        this.crSync.changeAlertState(
          alert.userId,
          alert.alertId,
          CrAlertState.Available
        );
      }

      const previousAllowTransition = alert.allowTransition;
      alert.allowTransition = false;
      alert.transitional = true;

      const timer = setTimeout(() => {
        const ix = event.container.data.findIndex(
          (a) => a.alertId === alert.alertId
        );
        if (ix !== -1) {
          event.container.data.splice(ix, 1);
          event.previousContainer.data.push(alert);
        }
        delete this.transitionalAlerts[alert.alertId];
        alert.allowTransition = previousAllowTransition;
        alert.transitional = false;
      }, 5000);
      this.transitionalAlerts[alert.alertId] = timer;

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  getFormattedRemainingTime(seconds: number): string {
    // Display hours and minutes if remaining time is more than a 59 minutes
    if (seconds > 3600 - 60) {
      let h = Math.floor(seconds / 3600);
      let m = Math.ceil((seconds % 3600) / 60);

      // If minutes are 60, add one hour and set minutes to 0 to display correctly
      if (m === 60) {
        h++;
        m = 0;
      }

      return `${h} h ${m} min`;
    }

    // Show only minutes if remaining time is more than a minute
    if (seconds > 60) {
      const m = Math.ceil((seconds % 3600) / 60);
      return `${m} min`;
    }

    // If visit is on grace period (remaining time is negative) display 0 minutes
    if (seconds < 0) {
      return `0 sec`;
    }

    // Display seconds if none of the above matches
    return `${seconds} sec`;
  }

  startVisit(visit: any): void {
    this.videoServerService.startVisit(visit).subscribe((res) => {});
  }

  completeStartedVisit(visit: any): void {
    this.videoServerService.completeVisit(visit).subscribe((res) => {});
  }

  generateAlertFromVisit(visit: any): void {
    this.api.createNewAlert(visit.lyraId, 1, 2).subscribe((res) => {
      this.completeStartedVisit(visit);
    });
  }

  cancelStartedVisit(visit: any): void {
    this.videoServerService.cancelStartedVisit(visit).subscribe((res) => {});
  }

  snoozeVisit(visit: any): void {
    if (visit) {
      this.videoServerService.snoozeVisit(visit).subscribe((res) => { });
    }
  }

  /** This has been replaced by icons.service in alerts */
  getAlertFaIcon(alert: CrAlert): string {
    switch (alert.deviceType) {
      case "06-2":
      case "33-255":
        return "fa-triangle-exclamation colorRed";
      case "34-1":
      case "34-2":
        return "fa-video colorLightBlue";
      case "34-4":
        if (alert.isCameraVisit) {
          return "fa-video colorLightBlue";
        }
        return "fa-bell colorYellow";
      case "89-1":
      case "10-2":
      case "88-1":
        return "fa-fire colorRed";
      case "10-3":
      case "10-5":
      case "10-9":
      case "32-1":
      case "32-3":
        return "fa-door-open colorOrange";
      default:
        return "fa-bell colorYellow";
    }
  }

  notifyAlert(alert: CrAlert) {
    // show notification, if supported and granted
    if ("Notification" in window && Notification.permission === "granted") {
      const icon = this.pickUpAlertIcon.transform(alert);
      const options = {
        icon: icon,
      };
      new Notification(alert.header, options);
    }
    // play audio
    new Audio("/assets/sounds/1.mp3").play();
  }

  updateAllForwardedAlerts(alerts: CrAlert[]) {
    this.forwardedAlerts = [];
    this.teamsForwardedAlerts = [];
    alerts.forEach((alert) => {
      if (this._isUserOrAlias(alert.reRoutedBy)) {
        this.forwardedAlerts.push(alert);
      } else {
        this.teamsForwardedAlerts.push(alert);
      }
    });
  }

  updateOneForwardedAlert(alert: CrAlert) {
    if (this._isUserOrAlias(alert.reRoutedBy)) {
      const ix: number = this.forwardedAlerts.findIndex(
        (a) => a.alertId === alert.alertId
      );
      if (ix !== -1) {
        alert.noteCache = this.forwardedAlerts[ix].noteCache;
        this.forwardedAlerts[ix] = alert;
      } else {
        this.forwardedAlerts.push(alert);
      }
    } else {
      const ix: number = this.teamsForwardedAlerts.findIndex(
        (a) => a.alertId === alert.alertId
      );
      if (ix !== -1) {
        this.teamsForwardedAlerts[ix] = alert;
      } else {
        this.teamsForwardedAlerts.push(alert);
      }
    }
  }

  deleteOneForwardedAlert(alertId: string) {
    this.forwardedAlerts = this.forwardedAlerts.filter(
      (a) => a.alertId !== alertId
    );
    this.teamsForwardedAlerts = this.teamsForwardedAlerts.filter(
      (a) => a.alertId !== alertId
    );
  }

  alertIsNightowlVisit = (alert: CrAlert): boolean => {
    return alert.deviceType === DeviceClass.NightOwl && alert.isCameraVisit;
  };

  nightowlSupervisionPaused = (alert: CrAlert): boolean => {
    return (
      alert.deviceType === NIGHTOWL_SUPERVISION_PAUSED.devicePrefixClass &&
      alert.alertGroup === NIGHTOWL_SUPERVISION_PAUSED.alarmGroup &&
      alert.alertNode === NIGHTOWL_SUPERVISION_PAUSED.alarmNode
    );
  };

  // it would be good to place into videovisits service

  notifyNewVisits(count: number): void {
    if (count > 0) {
      this.notificationService.notifyNewVisits(count);
    }
  }

  // Set video playback status to running for logging
  setVideoPlayback(visit: any, cameraId: string | null | undefined, started: boolean, completed: boolean): void {
    this.videoServerService.setVideoPlayback(visit, cameraId, started, completed);
  }

  removeFromVideoPlayback(visitId:string): void {
    this.videoServerService.removeFromVideoPlayback(visitId);
  }

  updateCameraVisits(activeVisits: ActiveCameraVisit[]) {
    let newVisitCount = 0;
    activeVisits.forEach((v) => {
      // Find correct alias user from localstorage to get company and location information
      const alias = JSON.parse(
        localStorage.getItem("alertServerUser") || '{}'
      ).aliases.find((x) => x.companyId === v.companyId);
      const location = alias.locations.find((x) => x.id === v.locationId);

      // Get needed information about the visit object
      const aliasFeatures = JSON.parse(localStorage.getItem('aliasFeatures') || '[]');
      const visit = {
        id: v.id,
        type: v.visitType,
        start: v.scheduleItem?.startDate,
        end: v.scheduleItem?.endDate,
        name: v.displayName,
        companyId: v.companyId,
        companyName: alias.companyName,
        locationId: v.locationId,
        locationName: location ? location.name : alias.companyName,
        userGroups: v.scheduleItem?.userGroups,
        rtsp: encodeURIComponent(v.cameras[0]?.rtspUrl),
        lyraId: v.cameras[0]?.lyraId,
        token: v.userToken,
        startedBy: v.startedBy,
        completedBy: v.completedBy ? v.completedBy : "",
        timeRemaining: moment(v.scheduleItem?.endDate).diff(moment(), "seconds"),
        snoozed: v.scheduleItem.snoozed || v.scheduleItem.snoozedDeleteWhenDone,
        snoozable: aliasFeatures.indexOf("SnoozableVisits_" + v.companyId) !== -1,
        cameraGroupId: v.scheduleItem.cameraGroupId,
        cameras: (v.cameras ?? []).map(camera => ({
          ...camera,
          videoUrl:
            this.videoServerService.setVideoUrl(camera.rtspUrl, v.userToken, localStorage.getItem('language') || 'en', v.id, v.scheduleItem.cameraGroupId, camera.deviceId)
        })),
        timeZone: v.scheduleItem.time.timeZone,
        gracePeriod: v.scheduleItem.gracePeriod,
        weekminuteStop: v.scheduleItem.stopTime.weekminute,
        previousVisitTimeUtc: v.previousVisitTimeUtc,
        address: v.address,
        postOffice: v.postOffice,
        postCode: v.postCode,
        origonGsmNumber: v.origonGsmNumber
      };

      // Get all user groups from aliases
      const user = JSON.parse(localStorage.getItem("user") || '{}');
      const aliasArray: any[] = [];
      if (user.aliasList && user.aliasList.length > 0) {
        user.aliasList.forEach((e) => {
          if (e.userGroups && e.userGroups.length > 0) {
            e.userGroups.forEach((g) => {
              aliasArray.push(g);
            });
          }
        });
      }
      // Check that logged in user is in same group as visit. If not then skip adding visit
      if (!visit.userGroups?.some((x) => aliasArray.includes(x))) {
        return;
      }

      // Check if visit has been started by someone. Place visits not started by anyone to incoming visits. If visit has been started by logged in operator place it in started visits
      if (visit.startedBy === "" && visit.completedBy === "") {
        // Add visit into list of incoming visits if it does not exists there yet
        this.incomingVisits.findIndex((x) => x.id == visit.id) === -1 &&
          this.incomingVisits.push(visit) &&
          newVisitCount++;
        // Make sure that visit does not exists in started visits list either
        const ix = this.startedVisits.findIndex((x) => x.id === visit.id);
        if (ix !== -1) this.startedVisits.splice(ix, 1);
      } else if (visit.startedBy !== "" && visit.completedBy === "") {
        // Check if visit has been started by any one the logged in alias accounts
        const aliases = JSON.parse(
          localStorage.getItem("alertServerUser") || '{}'
        ).aliases;
        aliases.forEach((a) => {
          if (a.userId === visit.startedBy) {
            this.startedVisits.findIndex((x) => x.id == visit.id) === -1 &&
              this.startedVisits.push(visit);
          }
        });
        // Finally remove the started visit from incoming visits list
        const ix = this.incomingVisits.findIndex((x) => x.id === visit.id);
        if (ix !== -1) this.incomingVisits.splice(ix, 1);
      } else {
        // Remove visit from both lists since it has been already completed or does not belong to any lists for other reasons
        let i = this.incomingVisits.findIndex((x) => x.id === visit.id);
        if (i !== -1) this.incomingVisits.splice(i, 1);

        i = this.startedVisits.findIndex((x) => x.id === visit.id);
        if (i !== -1) this.startedVisits.splice(i, 1);
      }
    });

    // Make sure that both lists contain only valid visits so no visits has been removed
    this.incomingVisits.forEach((iVisit) => {
      const ix = activeVisits.findIndex((x) => x.id === iVisit.id);
      if (ix === -1)
        this.incomingVisits.splice(
          this.incomingVisits.findIndex((x) => x.id === iVisit.id),
          1
        );
    });
    this.startedVisits.forEach((sVisit) => {
      const ix = activeVisits.findIndex((x) => x.id === sVisit.id);
      if (ix === -1)
        this.startedVisits.splice(
          this.startedVisits.findIndex((x) => x.id === sVisit.id),
          1
        );
    });

    // Order both lists based on the remaining time
    this.incomingVisits.sort((a, b) => a.timeRemaining - b.timeRemaining);
    this.startedVisits.sort((a, b) => a.timeRemaining - b.timeRemaining);

    this.notifyNewVisits(newVisitCount);
  }
}
